/** Analytics Portal V1 — the eight approved empty states (Phase 9, plan §12.7).
 * Copy is fixed and promises nothing; suggestions are actions, not guarantees. */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarRange, Inbox, Loader2, Lock, ShieldOff, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATES = {
  "no-data-yet": {
    icon: Sparkles,
    title: "No analytics yet",
    body: "Your analytics will appear as people discover and engage with this profile.",
    // Every hint links to the page where the action is actually taken.
    hints: [
      { label: "Complete your profile", to: "/dashboard/profile", id: "profile" },
      { label: "Publish an event", to: "/dashboard/events", id: "event" },
      { label: "Add a resource", to: "/dashboard/resources", id: "resource" },
      { label: "Add a business", to: "/dashboard/businesses", id: "business" },
    ],
  },
  "no-data-in-period": {
    icon: CalendarRange,
    title: "No activity in this period",
    body: "Try a wider date range to see earlier activity.",
    action: "Choose another range",
  },
  processing: {
    icon: Loader2,
    title: "Still crunching the most recent data",
    body: "The latest activity is being processed. Refresh in a few minutes.",
  },
  "no-permission": {
    icon: ShieldOff,
    title: "No analytics access",
    body: "You don't have analytics access for this entity.",
  },
  "subscription-required": {
    icon: Lock,
    title: "Analytics is a paid feature",
    body: "Analytics is included with PRO and CAPITAL.",
  },
  "entity-deleted": {
    icon: Trash2,
    title: "Analytics unavailable",
    body: "This entity was deleted and its analytics are no longer available.",
  },
  "no-entities": {
    icon: Inbox,
    title: "Nothing to analyse yet",
    body: "Create or join a profile, event or resource and its analytics will show up here.",
  },
  "service-unavailable": {
    icon: ShieldOff,
    title: "Analytics is unavailable",
    body: "Analytics isn't available right now. Please try again later.",
  },
};

export const EmptyState = ({ state = "no-data-yet", onAction, actionLabel, body }) => {
  const config = STATES[state] || STATES["no-data-yet"];
  const Icon = config.icon;
  return (
    <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-border bg-secondary/40 px-5 py-8"
         data-testid={`analytics-empty-${state}`} role="status">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-card">
        <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
      </span>
      <div>
        <h3 className="text-base font-[620] text-foreground">{config.title}</h3>
        <p className="mt-1 max-w-xl text-sm text-muted-foreground">{body || config.body}</p>
      </div>
      {config.hints && (
        <ul className="mt-1 flex flex-wrap gap-2">
          {config.hints.map((hint) => (
            <li key={hint.id}>
              <Link to={hint.to}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground transition-colors duration-150 hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    data-testid={`analytics-empty-hint-${hint.id}`}>
                {hint.label}
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {(config.action || actionLabel) && onAction && (
        <Button variant="secondary" size="sm" onClick={onAction}
                data-testid={`analytics-empty-action-${state}`}>
          {actionLabel || config.action}
        </Button>
      )}
    </div>
  );
};
